import { useCallback, useEffect, useState } from 'react';
import { useCouponsStatistics } from '../../../hooks/useStatistics';
import { GERENCIALES_REPORT_OPTIONS, GERENCIALES_REPORT_TYPE } from '../constants/gerencialesConstants';

/**
 * Hook para obtener y mantener estadisticas gerenciales de contratos.
 * @returns {Object} Estado y acciones para la vista Gerenciales.
 */
export const useGerencialesStatistics = () => {
  const { mutateAsync: fetchStatistics } = useCouponsStatistics();
  const [reportType, setReportType] = useState(GERENCIALES_REPORT_TYPE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);
  const [header, setHeader] = useState(null);

  /**
   * Solicita al backend las estadisticas gerenciales.
   * @returns {Promise<void>} Promesa de carga.
   */
  const loadStatistics = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetchStatistics({ reportType });
      setResults(response?.results || null);
      setHeader(response?.header || null);
    } catch (fetchError) {
      console.error('Error fetching managerial statistics:', fetchError);
      setError('Error al obtener las estadisticas.');
      setResults(null);
      setHeader(null);
    } finally {
      setLoading(false);
    }
  }, [fetchStatistics, reportType]);

  useEffect(() => {
    loadStatistics();
  }, [loadStatistics]);

  /**
   * Maneja el cambio de tipo de reporte.
   * @param {import('react').ChangeEvent<HTMLInputElement>} event - Evento del select.
   * @returns {void} Actualiza el tipo de reporte.
   */
  const handleReportTypeChange = (event) => {
    const { value } = event.target;
    const isValid = GERENCIALES_REPORT_OPTIONS.some((option) => option.value === value);
    if (!isValid) return;
    setReportType(value);
  };

  return {
    reportType,
    loading,
    error,
    results,
    header,
    reload: loadStatistics,
    handleReportTypeChange,
  };
};
